import { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, Clock, Tag, FolderOpen, Zap, User, Target, Folder, Loader2, Plus, CalendarDays, ChevronDown, AlertTriangle, Repeat } from 'lucide-react';
import Modal from './Modal';
import DelegateInput from './DelegateInput';
import { TASK_CONTEXTS, ENERGY_LEVELS, TIME_ESTIMATES } from '../hooks/useTasks';
import { formatEventTime } from '../hooks/useGoogleCalendar';
import { calculateBlockCapacity, formatRemainingCapacity, parseTimeEstimate } from '../utils/timeBlockCapacity';

// Status is now computed from task properties, not set manually
// Status changes happen via quick actions in the task list, not in this modal

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'text-text-tertiary' },
  { value: 'normal', label: 'Normal', color: 'text-text-secondary' },
  { value: 'high', label: 'High', color: 'text-red-400' }
];

export default function TaskModal({
  isOpen,
  onClose,
  onSave,
  editItem = null,
  timeBlocks = [],
  projects = [],
  goals = [],
  delegates = [],
  gcalSignedIn = false,
  getTimeBlocksForDate = null,
  createTimeBlock = null,
  timeBlockNames = [],
  getTasksForBlock = null
}) {
  const [formData, setFormData] = useState({
    title: '',
    notes: '',
    // Status is computed from properties, not stored
    context: null,
    projectId: null,
    goalId: null,
    timeBlockId: null,
    gcalTimeBlockId: null,
    gcalTimeBlockName: null,
    dueDate: '',
    scheduledDate: '',
    energy: null,
    timeEstimate: null,
    priority: 'normal',
    waitingFor: '',
    // Delegation fields
    delegatedTo: null,
    delegateDate: null,
    followUpDate: null,
    // Someday flag
    isSomeday: false,
    // Recurring fields
    isRecurring: false,
    recurrencePattern: null,
    recurrenceDay: null
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [gcalBlocks, setGcalBlocks] = useState([]);
  const [gcalLoading, setGcalLoading] = useState(false);
  const [showCreateBlock, setShowCreateBlock] = useState(false);
  const [newBlockName, setNewBlockName] = useState('');
  const [newBlockStart, setNewBlockStart] = useState('09:00');
  const [newBlockEnd, setNewBlockEnd] = useState('10:00');
  const [creatingBlock, setCreatingBlock] = useState(false);

  // Calculate overbooking warning when block or time estimate changes
  const overbookingWarning = useMemo(() => {
    if (!formData.gcalTimeBlockId || !getTasksForBlock) return null;

    const selectedBlock = gcalBlocks.find(b => b.id === formData.gcalTimeBlockId);
    if (!selectedBlock) return null;

    const blockTasks = getTasksForBlock(formData.gcalTimeBlockId);
    // Exclude current task if editing
    const otherTasks = editItem
      ? blockTasks.filter(t => t.id !== editItem.id)
      : blockTasks;

    const currentTaskMinutes = parseTimeEstimate(formData.timeEstimate);
    const capacity = calculateBlockCapacity(selectedBlock, otherTasks);

    const afterAddingTask = capacity.remainingMinutes - currentTaskMinutes;

    if (afterAddingTask < 0) {
      return {
        overbooked: Math.abs(afterAddingTask),
        blockName: selectedBlock.summary
      };
    }
    return null;
  }, [formData.gcalTimeBlockId, formData.timeEstimate, gcalBlocks, editItem, getTasksForBlock]);

  // Fetch GCal time blocks when scheduled date changes
  const fetchGcalBlocks = useCallback(async (date) => {
    if (!gcalSignedIn || !getTimeBlocksForDate || !date) {
      setGcalBlocks([]);
      return;
    }

    setGcalLoading(true);
    try {
      const blocks = await getTimeBlocksForDate(date);
      console.log('TaskModal: fetched time blocks for', date, ':', blocks.length, 'blocks', blocks.map(e => e.summary));
      setGcalBlocks(blocks);
    } catch (err) {
      console.error('Failed to fetch time blocks:', err);
      setGcalBlocks([]);
    } finally {
      setGcalLoading(false);
    }
  }, [gcalSignedIn, getTimeBlocksForDate]);

  // Handle creating a new time block
  const handleCreateBlock = async () => {
    if (!createTimeBlock || !newBlockName || !formData.scheduledDate) return;

    setCreatingBlock(true);
    try {
      const event = await createTimeBlock(newBlockName, formData.scheduledDate, newBlockStart, newBlockEnd);
      if (event) {
        // Refresh the blocks list
        await fetchGcalBlocks(formData.scheduledDate);
        // Select the newly created block
        setFormData(prev => ({
          ...prev,
          gcalTimeBlockId: event.id,
          gcalTimeBlockName: event.summary
        }));
        setShowCreateBlock(false);
        setNewBlockName('');
      }
    } catch (err) {
      console.error('Failed to create time block:', err);
    } finally {
      setCreatingBlock(false);
    }
  };

  useEffect(() => {
    if (editItem) {
      setFormData({
        title: editItem.title || '',
        notes: editItem.notes || '',
        context: editItem.context || null,
        projectId: editItem.projectId || null,
        goalId: editItem.goalId || null,
        timeBlockId: editItem.timeBlockId || null,
        gcalTimeBlockId: editItem.gcalTimeBlockId || null,
        gcalTimeBlockName: editItem.gcalTimeBlockName || null,
        dueDate: editItem.dueDate || '',
        scheduledDate: editItem.scheduledDate || '',
        energy: editItem.energy || null,
        timeEstimate: editItem.timeEstimate || null,
        priority: editItem.priority || 'normal',
        waitingFor: editItem.waitingFor || '',
        // Delegation fields
        delegatedTo: editItem.delegatedTo || null,
        delegateDate: editItem.delegateDate || null,
        followUpDate: editItem.followUpDate || null,
        // Someday flag
        isSomeday: editItem.isSomeday || false,
        // Recurring fields
        isRecurring: editItem.isRecurring || false,
        recurrencePattern: editItem.recurrencePattern || null,
        recurrenceDay: editItem.recurrenceDay !== undefined ? editItem.recurrenceDay : null
      });
      // Show advanced if any advanced fields are set (time/energy/recurring only - project/goal now always visible)
      if (editItem.energy || editItem.timeEstimate || editItem.isRecurring) {
        setShowAdvanced(true);
      }
      // Fetch GCal blocks if there's a scheduled date
      if (editItem.scheduledDate && gcalSignedIn) {
        fetchGcalBlocks(editItem.scheduledDate);
      }
    } else {
      setFormData({
        title: '',
        notes: '',
        context: null,
        projectId: null,
        goalId: null,
        timeBlockId: null,
        gcalTimeBlockId: null,
        gcalTimeBlockName: null,
        dueDate: '',
        scheduledDate: '',
        energy: null,
        timeEstimate: null,
        priority: 'normal',
        waitingFor: '',
        // Delegation fields
        delegatedTo: null,
        delegateDate: null,
        followUpDate: null,
        // Someday flag
        isSomeday: false,
        // Recurring fields
        isRecurring: false,
        recurrencePattern: null,
        recurrenceDay: null
      });
      setShowAdvanced(false);
      setGcalBlocks([]);
    }
  }, [editItem, isOpen, gcalSignedIn, fetchGcalBlocks]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    const taskData = {
      ...formData,
      dueDate: formData.dueDate || null,
      scheduledDate: formData.scheduledDate || null
    };

    onSave(taskData);
    onClose();
  };

  // Check if task has waiting/delegation info (to show those fields)
  const hasWaitingInfo = !!(formData.waitingFor || formData.delegatedTo || formData.followUpDate);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editItem ? 'Edit Task' : 'Add Task'}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3 md:gap-4">
        {/* Title */}
        <div>
          <input
            type="text"
            placeholder="What needs to be done?"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            autoFocus
            className="w-full py-2 md:py-3 px-3 md:px-4 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all placeholder:text-text-muted"
          />
        </div>

        {/* Notes */}
        <div>
          <textarea
            placeholder="Add notes (optional)"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={2}
            className="w-full py-2 md:py-3 px-3 md:px-4 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all placeholder:text-text-muted resize-none"
          />
        </div>

        {/* Quick Options Row - Status removed (now computed from properties) */}
        <div className="flex flex-wrap gap-2">
          {/* Context */}
          <div className="relative">
            <select
              value={formData.context || ''}
              onChange={(e) => setFormData({ ...formData, context: e.target.value || null })}
              className="appearance-none py-2 pl-3 pr-8 bg-surface-card border border-border rounded-lg text-xs text-text-primary cursor-pointer hover:border-accent focus:border-accent focus:ring-2 focus:ring-accent-muted outline-none transition-all"
            >
              <option value="">No Context</option>
              {TASK_CONTEXTS.map((ctx) => (
                <option key={ctx.id} value={ctx.id}>
                  {ctx.label}
                </option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
          </div>

          {/* Priority */}
          <div className="relative">
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="appearance-none py-2 pl-3 pr-8 bg-surface-card border border-border rounded-lg text-xs text-text-primary cursor-pointer hover:border-accent focus:border-accent focus:ring-2 focus:ring-accent-muted outline-none transition-all"
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} Priority
                </option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
          </div>
        </div>

        {/* Delegation & Follow-up (shown when task has waiting info, set via quick actions) */}
        {hasWaitingInfo && (
          <div className="p-3 bg-amber-500/5 rounded-lg border border-amber-500/20 space-y-3">
            <div className="flex items-center gap-2">
              <User size={14} className="text-amber-400" />
              <span className="text-xs font-medium text-amber-400">Delegation & Follow-up</span>
            </div>

            {/* Delegated To */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-text-secondary w-20">Delegated to:</span>
              <DelegateInput
                value={formData.delegatedTo || ''}
                onChange={(value) => setFormData({ ...formData, delegatedTo: value || null })}
                delegates={delegates}
                placeholder="Who is this delegated to?"
                className="flex-1"
              />
            </div>

            {/* Waiting For */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-text-secondary w-20">Waiting for:</span>
              <input
                type="text"
                placeholder="What are you waiting for?"
                value={formData.waitingFor}
                onChange={(e) => setFormData({ ...formData, waitingFor: e.target.value })}
                className="flex-1 py-1.5 px-2 bg-surface-elevated border border-border rounded-lg text-xs text-text-primary"
              />
            </div>

            {/* Dates Row */}
            <div className="grid grid-cols-2 gap-2">
              {/* Delegate Date */}
              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-text-secondary">Delegated on:</span>
                <input
                  type="date"
                  value={formData.delegateDate || ''}
                  onChange={(e) => setFormData({ ...formData, delegateDate: e.target.value || null })}
                  className="py-1.5 px-2 bg-surface-elevated border border-border rounded-lg text-xs text-text-primary"
                />
              </div>

              {/* Follow-up Date */}
              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-amber-400 font-medium">Follow-up on:</span>
                <input
                  type="date"
                  value={formData.followUpDate || ''}
                  onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value || null })}
                  className="py-1.5 px-2 bg-surface-elevated border border-amber-500/30 rounded-lg text-xs text-text-primary"
                />
              </div>
            </div>

            {formData.followUpDate && (
              <p className="text-[10px] text-amber-400/70">
                This task will appear in your Today view on {new Date(formData.followUpDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </p>
            )}
          </div>
        )}

        {/* Dates Row */}
        <div className="grid grid-cols-2 gap-2">
          {/* Scheduled Date */}
          <div className="flex flex-col gap-1 p-2 md:p-3 bg-surface-card rounded-lg border border-border">
            <div className="flex items-center gap-1.5">
              <Calendar size={14} className="text-accent" />
              <span className="text-[10px] md:text-xs text-text-secondary">Scheduled</span>
            </div>
            <input
              type="date"
              value={formData.scheduledDate}
              onChange={(e) => {
                const newDate = e.target.value;
                setFormData({ ...formData, scheduledDate: newDate, gcalTimeBlockId: null, gcalTimeBlockName: null });
                if (newDate && gcalSignedIn) {
                  fetchGcalBlocks(newDate);
                } else {
                  setGcalBlocks([]);
                }
              }}
              className="py-1 md:py-1.5 px-2 bg-surface-elevated border border-border rounded-lg text-xs text-text-primary"
            />
          </div>

          {/* Due Date */}
          <div className="flex flex-col gap-1 p-2 md:p-3 bg-surface-card rounded-lg border border-border">
            <div className="flex items-center gap-1.5">
              <Calendar size={14} className="text-red-400" />
              <span className="text-[10px] md:text-xs text-text-secondary">Due Date</span>
            </div>
            <input
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              className="py-1 md:py-1.5 px-2 bg-surface-elevated border border-border rounded-lg text-xs text-text-primary"
            />
          </div>
        </div>

        {/* Google Calendar Time Block Picker */}
        {gcalSignedIn && formData.scheduledDate && (
          <div className="p-2 md:p-3 bg-surface-card rounded-lg border border-border">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <CalendarDays size={14} className="text-green-500" />
                <span className="text-xs text-text-secondary">Time Block</span>
              </div>
              {gcalLoading && <Loader2 size={14} className="text-accent animate-spin" />}
            </div>

            {gcalBlocks.length > 0 ? (
              <div className="space-y-1.5">
                {gcalBlocks.map((block) => {
                  // Calculate capacity for this block
                  const blockTasks = getTasksForBlock ? getTasksForBlock(block.id) : [];
                  // Exclude current task if editing
                  const tasksExcludingCurrent = editItem
                    ? blockTasks.filter(t => t.id !== editItem.id)
                    : blockTasks;
                  const capacity = calculateBlockCapacity(block, tasksExcludingCurrent);

                  return (
                    <button
                      key={block.id}
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        gcalTimeBlockId: block.id,
                        gcalTimeBlockName: block.summary,
                        timeBlockId: null
                      })}
                      className={`w-full flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-all ${
                        formData.gcalTimeBlockId === block.id
                          ? 'bg-green-500/10 border-green-500/50 text-green-500'
                          : capacity.isOverbooked
                            ? 'bg-red-500/5 border-red-500/30 text-text-primary hover:border-red-500/50'
                            : 'bg-surface-elevated border-border text-text-primary hover:border-green-500/30'
                      }`}
                    >
                      <div className="flex flex-col items-start">
                        <span className="text-xs font-medium">{block.summary}</span>
                        <span className="text-[10px] text-text-tertiary">{formatEventTime(block)}</span>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className={`text-[10px] font-medium ${
                          capacity.remainingMinutes <= 0 ? 'text-red-400' :
                          capacity.remainingMinutes <= 30 ? 'text-amber-400' :
                          'text-green-500'
                        }`}>
                          {formatRemainingCapacity(capacity.remainingMinutes)}
                        </span>
                        <span className="text-[9px] text-text-muted">
                          {capacity.fillPercentage}% full
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : !gcalLoading ? (
              <p className="text-[10px] text-text-tertiary italic">No time blocks found for this date</p>
            ) : null}

            {/* Create New Time Block */}
            {!showCreateBlock ? (
              <button
                type="button"
                onClick={() => setShowCreateBlock(true)}
                className="mt-2 flex items-center gap-1.5 text-[10px] text-accent hover:text-accent/80 cursor-pointer bg-transparent border-none"
              >
                <Plus size={12} />
                Create new time block
              </button>
            ) : (
              <div className="mt-2 p-2 bg-surface-elevated rounded-lg border border-border space-y-2">
                <select
                  value={newBlockName}
                  onChange={(e) => setNewBlockName(e.target.value)}
                  className="w-full py-1.5 px-2 bg-surface-card border border-border rounded text-xs text-text-primary cursor-pointer"
                >
                  <option value="">Select block type...</option>
                  {timeBlockNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <input
                    type="time"
                    value={newBlockStart}
                    onChange={(e) => setNewBlockStart(e.target.value)}
                    className="flex-1 py-1.5 px-2 bg-surface-card border border-border rounded text-xs text-text-primary"
                  />
                  <span className="text-text-tertiary text-xs self-center">to</span>
                  <input
                    type="time"
                    value={newBlockEnd}
                    onChange={(e) => setNewBlockEnd(e.target.value)}
                    className="flex-1 py-1.5 px-2 bg-surface-card border border-border rounded text-xs text-text-primary"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateBlock(false)}
                    className="flex-1 py-1.5 text-[10px] bg-surface-card text-text-secondary rounded border border-border cursor-pointer hover:bg-surface-hover"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateBlock}
                    disabled={!newBlockName || creatingBlock}
                    className="flex-1 py-1.5 text-[10px] bg-green-500 text-white rounded border-none cursor-pointer hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                  >
                    {creatingBlock ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                    Create
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Project & Goal - Always Visible */}
        <div className="grid grid-cols-2 gap-2">
          {/* Project */}
          <div className="flex flex-col gap-1 p-2 md:p-3 bg-surface-card rounded-lg border border-border">
            <div className="flex items-center gap-1.5">
              <Folder size={14} className="text-purple-400" />
              <span className="text-[10px] md:text-xs text-text-secondary">Project</span>
            </div>
            <select
              value={formData.projectId || ''}
              onChange={(e) => setFormData({ ...formData, projectId: e.target.value || null, goalId: null })}
              className="py-1 md:py-1.5 px-2 bg-surface-elevated border border-border rounded-lg text-xs text-text-primary cursor-pointer"
            >
              <option value="">None</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Goal (only if no project selected) */}
          <div className="flex flex-col gap-1 p-2 md:p-3 bg-surface-card rounded-lg border border-border">
            <div className="flex items-center gap-1.5">
              <Target size={14} className="text-green-400" />
              <span className="text-[10px] md:text-xs text-text-secondary">Goal</span>
            </div>
            <select
              value={formData.goalId || ''}
              onChange={(e) => setFormData({ ...formData, goalId: e.target.value || null })}
              disabled={!!formData.projectId}
              className={`py-1 md:py-1.5 px-2 bg-surface-elevated border border-border rounded-lg text-xs text-text-primary cursor-pointer ${formData.projectId ? 'opacity-50' : ''}`}
            >
              <option value="">{formData.projectId ? 'Set via project' : 'None'}</option>
              {goals.filter(g => !g.completed).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Overbooking Warning */}
        {overbookingWarning && (
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-amber-500">
                This will overbook {overbookingWarning.blockName}
              </p>
              <p className="text-[10px] text-amber-400/80 mt-0.5">
                The block will be {overbookingWarning.overbooked} minutes over capacity.
                You can still save, but consider adjusting the time estimate or choosing another block.
              </p>
            </div>
          </div>
        )}

        {/* Legacy Time Block (only show if not using GCal or no date selected) */}
        {(!gcalSignedIn || !formData.scheduledDate) && timeBlocks.length > 0 && (
          <div className="flex items-center gap-2 p-2 md:p-3 bg-surface-card rounded-lg border border-border">
            <Clock size={16} className="text-weekly" />
            <span className="text-xs text-text-secondary">Time Block:</span>
            <select
              value={formData.timeBlockId || ''}
              onChange={(e) => setFormData({ ...formData, timeBlockId: e.target.value || null })}
              className="flex-1 py-1 md:py-1.5 px-2 bg-surface-elevated border border-border rounded-lg text-xs text-text-primary cursor-pointer"
            >
              <option value="">None</option>
              {timeBlocks.map((tb) => (
                <option key={tb.id} value={tb.id}>
                  {tb.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Advanced Options Toggle */}
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-xs text-accent hover:text-accent/80 cursor-pointer bg-transparent border-none text-left"
        >
          {showAdvanced ? '− Hide advanced options' : '+ Show advanced options'}
        </button>

        {/* Advanced Options */}
        {showAdvanced && (
          <div className="space-y-3 p-3 bg-surface-elevated/50 rounded-lg border border-border">
            {/* Time Estimate */}
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-text-tertiary" />
              <span className="text-xs text-text-secondary w-20">Time:</span>
              <select
                value={formData.timeEstimate || ''}
                onChange={(e) => setFormData({ ...formData, timeEstimate: e.target.value || null })}
                className="flex-1 py-1.5 px-2 bg-surface-card border border-border rounded-lg text-xs text-text-primary cursor-pointer"
              >
                <option value="">Not set</option>
                {TIME_ESTIMATES.map((te) => (
                  <option key={te.id} value={te.id}>
                    {te.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Energy Level */}
            <div className="flex items-center gap-2">
              <Zap size={14} className="text-text-tertiary" />
              <span className="text-xs text-text-secondary w-20">Energy:</span>
              <select
                value={formData.energy || ''}
                onChange={(e) => setFormData({ ...formData, energy: e.target.value || null })}
                className="flex-1 py-1.5 px-2 bg-surface-card border border-border rounded-lg text-xs text-text-primary cursor-pointer"
              >
                <option value="">Not set</option>
                {ENERGY_LEVELS.map((el) => (
                  <option key={el.id} value={el.id}>
                    {el.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Recurring Task */}
            <div className="pt-2 border-t border-border">
              <div className="flex items-center gap-2">
                <Repeat size={14} className={formData.isRecurring ? 'text-purple-400' : 'text-text-tertiary'} />
                <span className="text-xs text-text-secondary w-20">Recurring:</span>
                <button
                  type="button"
                  onClick={() => setFormData({
                    ...formData,
                    isRecurring: !formData.isRecurring,
                    recurrencePattern: !formData.isRecurring ? 'weekly' : null,
                    recurrenceDay: !formData.isRecurring ? new Date().getDay() : null
                  })}
                  className={`px-3 py-1.5 text-xs rounded-lg border cursor-pointer transition-all ${
                    formData.isRecurring
                      ? 'bg-purple-500/10 border-purple-500/50 text-purple-400'
                      : 'bg-surface-card border-border text-text-secondary hover:border-purple-500/30'
                  }`}
                >
                  {formData.isRecurring ? 'Enabled' : 'Off'}
                </button>
              </div>

              {formData.isRecurring && (
                <div className="mt-3 p-3 bg-purple-500/5 rounded-lg border border-purple-500/20 space-y-3">
                  {/* Recurrence Pattern */}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-text-secondary w-16">Repeats:</span>
                    <select
                      value={formData.recurrencePattern || 'weekly'}
                      onChange={(e) => setFormData({ ...formData, recurrencePattern: e.target.value })}
                      className="flex-1 py-1.5 px-2 bg-surface-elevated border border-border rounded-lg text-xs text-text-primary cursor-pointer"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="daily">Daily</option>
                    </select>
                  </div>

                  {/* Day Picker (for weekly) */}
                  {formData.recurrencePattern === 'weekly' && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-text-secondary w-16">On:</span>
                      <div className="flex gap-1">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                          <button
                            key={day}
                            type="button"
                            onClick={() => setFormData({ ...formData, recurrenceDay: index })}
                            className={`w-8 h-8 text-[10px] rounded-lg border cursor-pointer transition-all ${
                              formData.recurrenceDay === index
                                ? 'bg-purple-500 border-purple-500 text-white'
                                : 'bg-surface-elevated border-border text-text-secondary hover:border-purple-500/30'
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-[10px] text-purple-400/70">
                    {formData.recurrencePattern === 'weekly'
                      ? `A new instance will be created every ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][formData.recurrenceDay || 0]}`
                      : 'A new instance will be created every day'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="py-2 md:py-3 px-3 md:px-4 bg-surface-hover text-text-secondary border border-border rounded-xl text-xs md:text-sm font-medium cursor-pointer hover:bg-surface-active hover:text-text-primary transition-all"
          >
            Cancel
          </button>
          {!editItem && (
            <button
              type="button"
              onClick={async () => {
                if (!formData.title.trim()) return;
                const taskData = {
                  ...formData,
                  dueDate: formData.dueDate || null,
                  scheduledDate: formData.scheduledDate || null
                };
                await onSave(taskData);
                // Reset form but keep context/status for quick entry
                setFormData(prev => ({
                  ...prev,
                  title: '',
                  notes: '',
                  waitingFor: ''
                }));
              }}
              className="py-2 md:py-3 px-3 md:px-4 bg-surface-card text-text-primary border border-accent/50 rounded-xl text-xs md:text-sm font-medium cursor-pointer hover:bg-accent-muted transition-all"
            >
              Save & Add Another
            </button>
          )}
          <button
            type="submit"
            className="flex-1 py-2 md:py-3 px-3 md:px-4 bg-gradient-to-r from-accent to-orange-hover text-surface-base rounded-xl text-xs md:text-sm font-semibold cursor-pointer shadow-lg shadow-accent/20 hover:shadow-accent/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            {editItem ? 'Save Changes' : 'Add Task'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
